import { useState, useEffect } from "react";
import { prefixKey } from '@/utils/storagePrefix';

/**
 * Hook to manage favorite menu items
 * Automatically persists favorites to localStorage
 */
export function useFavorites(enabled: boolean = true) {
  const [favorites, setFavorites] = useState<string[]>([]);

  // Load favorites from localStorage on mount
  useEffect(() => {
    if (enabled) {
      const savedFavorites = localStorage.getItem(prefixKey('menuFavorites'));
      if (savedFavorites) {
        try {
          setFavorites(JSON.parse(savedFavorites));
        } catch (error) {
          console.error("Failed to parse favorites from localStorage:", error);
          setFavorites([]);
        }
      }
    }
  }, [enabled]);

  // Save favorites to localStorage whenever they change
  useEffect(() => {
    if (enabled) {
      localStorage.setItem(prefixKey('menuFavorites'), JSON.stringify(favorites));
    }
  }, [favorites, enabled]);

  const toggleFavorite = (itemId: string) => {
    if (!enabled) return;
    setFavorites((prev) =>
      prev.includes(itemId)
        ? prev.filter((id) => id !== itemId)
        : [...prev, itemId]
    );
  };

  const isFavorite = (itemId: string) => favorites.includes(itemId);

  const clearFavorites = () => {
    setFavorites([]);
    localStorage.removeItem(prefixKey('menuFavorites'));
  };

  return {
    favorites,
    toggleFavorite,
    isFavorite,
    clearFavorites,
  };
}
